import {
  type EnvironmentId,
  type ProjectId,
  type ProjectScript,
  type TerminalOpenInput,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import { projectScriptCwd, projectScriptRuntimeEnv } from "@cmmd-forge/shared/projectScripts";
import { DEFAULT_THREAD_TERMINAL_ID, type Thread } from "../types";
import { buildTaskContextModel, type TaskContextModel } from "./command-center/TaskContextSurface";

export const SCRIPT_TERMINAL_COLS = 120;
export const SCRIPT_TERMINAL_ROWS = 30;

export interface ProjectScriptTerminalLaunchPlan {
  readonly targetCwd: string;
  readonly targetTerminalId: string;
  readonly targetWorktreePath: string | null;
  readonly runtimeEnv: Record<string, string>;
  readonly shouldCreateNewTerminal: boolean;
  readonly storeAction: "new-terminal" | "set-active-terminal";
  readonly openInput: TerminalOpenInput;
}

export function buildProjectScriptTerminalLaunchPlan(input: {
  readonly activeProject: { readonly cwd: string };
  readonly activeThread: { readonly worktreePath: string | null };
  readonly activeThreadId: ThreadId;
  readonly script: ProjectScript;
  readonly gitCwd: string | null | undefined;
  readonly terminalState: {
    readonly activeTerminalId: string | null;
    readonly terminalIds: ReadonlyArray<string>;
    readonly runningTerminalIds: ReadonlyArray<string>;
  };
  readonly randomTerminalId: () => string;
  readonly options?:
    | {
        readonly cwd?: string;
        readonly env?: Record<string, string>;
        readonly worktreePath?: string | null;
        readonly preferNewTerminal?: boolean;
      }
    | undefined;
}): ProjectScriptTerminalLaunchPlan {
  const targetCwd = input.options?.cwd ?? input.gitCwd ?? input.activeProject.cwd;
  const baseTerminalId =
    input.terminalState.activeTerminalId ||
    input.terminalState.terminalIds[0] ||
    DEFAULT_THREAD_TERMINAL_ID;
  const isBaseTerminalBusy = input.terminalState.runningTerminalIds.includes(baseTerminalId);
  const shouldCreateNewTerminal = Boolean(input.options?.preferNewTerminal) || isBaseTerminalBusy;
  const targetTerminalId = shouldCreateNewTerminal ? input.randomTerminalId() : baseTerminalId;
  const targetWorktreePath = input.options?.worktreePath ?? input.activeThread.worktreePath ?? null;
  const runtimeEnv = projectScriptRuntimeEnv({
    project: {
      cwd: input.activeProject.cwd,
    },
    worktreePath: targetWorktreePath,
    ...(input.options?.env ? { extraEnv: input.options.env } : {}),
  });

  const openInput: TerminalOpenInput = shouldCreateNewTerminal
    ? {
        threadId: input.activeThreadId,
        terminalId: targetTerminalId,
        cwd: targetCwd,
        ...(targetWorktreePath !== null ? { worktreePath: targetWorktreePath } : {}),
        env: runtimeEnv,
        cols: SCRIPT_TERMINAL_COLS,
        rows: SCRIPT_TERMINAL_ROWS,
      }
    : {
        threadId: input.activeThreadId,
        terminalId: targetTerminalId,
        cwd: targetCwd,
        ...(targetWorktreePath !== null ? { worktreePath: targetWorktreePath } : {}),
        env: runtimeEnv,
      };

  return {
    targetCwd,
    targetTerminalId,
    targetWorktreePath,
    runtimeEnv,
    shouldCreateNewTerminal,
    storeAction: shouldCreateNewTerminal ? "new-terminal" : "set-active-terminal",
    openInput,
  };
}

export function resolvePersistentTerminalProjectRef(input: {
  serverThread:
    | { environmentId: EnvironmentId; projectId: ProjectId | null | undefined }
    | null
    | undefined;
  draftThread:
    | { environmentId: EnvironmentId; projectId: ProjectId | null | undefined }
    | null
    | undefined;
}): ReturnType<typeof scopeProjectRef> | null {
  const serverThreadEnvironmentId = input.serverThread?.environmentId;
  const serverThreadProjectId = input.serverThread?.projectId;
  if (serverThreadEnvironmentId && serverThreadProjectId) {
    return scopeProjectRef(serverThreadEnvironmentId, serverThreadProjectId);
  }
  const draftThreadEnvironmentId = input.draftThread?.environmentId;
  const draftThreadProjectId = input.draftThread?.projectId;
  if (draftThreadEnvironmentId && draftThreadProjectId) {
    return scopeProjectRef(draftThreadEnvironmentId, draftThreadProjectId);
  }
  return null;
}

export interface PersistentTerminalLaunchContextModel {
  readonly terminalId?: string | undefined;
  readonly cwd: string;
  readonly worktreePath: string | null;
  readonly runtimeEnv?: Record<string, string> | undefined;
}

export function resolveActivePersistentTerminalLaunchContext(input: {
  launchContext: PersistentTerminalLaunchContextModel | null;
  activeTerminalId: string | null | undefined;
}): PersistentTerminalLaunchContextModel | null {
  if (
    input.launchContext?.terminalId &&
    input.launchContext.terminalId !== input.activeTerminalId
  ) {
    return null;
  }
  return input.launchContext;
}

export function resolvePersistentTerminalWorktreePath(input: {
  activeLaunchContext: PersistentTerminalLaunchContextModel | null;
  serverThread: { worktreePath: string | null } | null | undefined;
  draftThread: { worktreePath: string | null } | null | undefined;
}): string | null {
  if (input.activeLaunchContext !== null) {
    return input.activeLaunchContext.worktreePath;
  }
  return input.serverThread?.worktreePath ?? input.draftThread?.worktreePath ?? null;
}

export function resolvePersistentTerminalCwd(input: {
  activeLaunchContext: PersistentTerminalLaunchContextModel | null;
  projectCwd: string | null;
  worktreePath: string | null;
}): string | null {
  return (
    input.activeLaunchContext?.cwd ??
    (input.projectCwd
      ? projectScriptCwd({
          project: { cwd: input.projectCwd },
          worktreePath: input.worktreePath,
        })
      : null)
  );
}

export function resolvePersistentTerminalRuntimeEnv(input: {
  activeLaunchContext: PersistentTerminalLaunchContextModel | null;
  projectCwd: string | null;
  worktreePath: string | null;
}): Record<string, string> {
  return (
    input.activeLaunchContext?.runtimeEnv ??
    (input.projectCwd
      ? projectScriptRuntimeEnv({
          project: { cwd: input.projectCwd },
          worktreePath: input.worktreePath,
        })
      : {})
  );
}

/**
 * The project half of a task-context model.
 *
 * Kept apart from the derivation so adding a field to a project's identity does
 * not add a decision point to the function that assembles the whole model.
 */
function taskContextProjectFields(
  project: {
    readonly title: string;
    readonly icon?: string | null;
    readonly iconUrl?: string | null;
  } | null,
): { projectName: string | null; projectIcon: string | null; projectIconUrl: string | null } {
  if (project === null) {
    return { projectName: null, projectIcon: null, projectIconUrl: null };
  }
  return {
    projectName: project.title,
    projectIcon: project.icon ?? null,
    projectIconUrl: project.iconUrl ?? null,
  };
}

export function deriveActiveTaskContextModel(input: {
  readonly activeThread:
    | {
        readonly anchorKind: Thread["anchorKind"];
        readonly anchorCmmdId: string | null;
      }
    | null
    | undefined;
  readonly cmmdTaskContextState: {
    readonly tasks: ReadonlyArray<{
      readonly id: string;
      readonly title: string;
      readonly status: string | null;
      readonly priority: string | null;
      readonly projectId: string | null;
      readonly campaignId: string | null;
      readonly workspaceName?: string | null;
    }>;
    readonly projects: ReadonlyArray<{
      readonly id: string;
      readonly title: string;
      readonly icon?: string | null;
      readonly iconUrl?: string | null;
    }>;
    readonly campaigns: ReadonlyArray<{ readonly id: string; readonly title: string }>;
    readonly workspace?: { readonly name?: string | null } | null;
    readonly source?: string | null;
  };
}): TaskContextModel | null {
  const taskId = input.activeThread?.anchorKind === "task" ? input.activeThread.anchorCmmdId : null;
  if (!taskId) return null;
  const task =
    input.cmmdTaskContextState.tasks.find((candidate) => candidate.id === taskId) ?? null;
  const project = task?.projectId
    ? (input.cmmdTaskContextState.projects.find((candidate) => candidate.id === task.projectId) ??
      null)
    : null;
  const campaignName = task?.campaignId
    ? (input.cmmdTaskContextState.campaigns.find((campaign) => campaign.id === task.campaignId)
        ?.title ?? null)
    : null;
  const source = input.cmmdTaskContextState.source?.replace(/\/+$/, "") ?? null;
  return buildTaskContextModel({
    taskId,
    task,
    ...taskContextProjectFields(project),
    campaignName,
    workspaceName: input.cmmdTaskContextState.workspace?.name ?? null,
    sourceUrl: source ? `${source}/tasks/${encodeURIComponent(taskId)}` : null,
    syncState: task ? "synced" : "pending",
  });
}
